import { useEffect, useRef, useState } from 'react';
import { Check, EyeOff, Send } from 'lucide-react';
import { Logo } from '../../components/Logo';
import GearTag from '../../components/treatment/GearTag';
import { cn, getStatusColor } from '../../lib/utils';
import { Reference } from './references';
import { prefersReducedMotion, usePlayOnView } from './motion';
import { ink, stock as paper } from './paper';

// A job's life on one record: the brief arrives, then the treatment; the gear
// the treatment needs and you don't own is sourced from suppliers in your crew
// book; the estimate fills in section by section, those hires among them, and
// is booked; the job wraps; and everything goes to Xero to be invoiced and
// paid there (the app never invoices). The papers are drawn: no client, no
// supplier's name, and no money figures.

const STAGES = [
  { key: 'brief', label: 'Brief', status: 'draft' },
  { key: 'treatment', label: 'Shot list', status: 'draft' },
  { key: 'suppliers', label: 'Hires', status: 'draft' },
  { key: 'estimate', label: 'Estimate', status: 'sent' },
  { key: 'booked', label: 'Booked', status: 'booked' },
  { key: 'wrapped', label: 'Wrapped', status: 'completed' },
  { key: 'xero', label: 'To Xero', status: 'xero' },
];
const STATUS_LABEL = { new: 'New', draft: 'Draft', sent: 'Sent', booked: 'Booked', completed: 'Completed', xero: 'Sent to Xero' };

const START = 700;
const STEP_MS = 1500;
const ROW_MS = 160; // between the rows of a paper filling in
const GATHER_MS = 450; // after "To Xero": the papers gather, then fly off
const FLY_MS = 900;
// Where each paper goes as they gather into one stack, on a wide screen (four across)
const GATHER = [
  'lg:translate-x-[calc(150%+1.5rem)] lg:rotate-6',
  'lg:translate-x-[calc(50%+0.5rem)] lg:rotate-2',
  'lg:-translate-x-[calc(50%+0.5rem)] lg:-rotate-3',
  'lg:-translate-x-[calc(150%+1.5rem)] lg:-rotate-1',
];
// The gear the treatment needs and you don't own, hired from your suppliers
const HIRES = ['Drone and pilot', 'Water housing'];

// One row of a paper, appearing in turn once the paper has arrived
function Row({ shown, index, instant, children, className }) {
  return (
    <span
      className={cn('flex items-center justify-between gap-2 text-xs transition-opacity duration-300', shown ? 'opacity-100' : 'opacity-0', className)}
      style={{ transitionDelay: shown && !instant ? `${300 + index * ROW_MS}ms` : '0ms' }}
    >
      {children}
    </span>
  );
}

// One paper on the job: an empty dashed place until it arrives, then it drops
// in, printed on one of the design system's card stocks (as a job card is),
// in that stock's ink so it always reads
function Doc({ title, stock, arrived, instant, children, stamp, place, gathered, onTop = false }) {
  return (
    <div className={cn('relative min-w-0', onTop && 'z-10')}>
      <div className={cn('absolute inset-0 rounded-tile border-2 border-dashed border-line-strong transition-opacity duration-300', arrived ? 'opacity-0' : 'opacity-100')} aria-hidden="true" />
      <div
        className={cn(
          'relative h-full rounded-tile p-4 flex flex-col gap-3 min-h-[212px]',
          !instant && 'transition-[transform,opacity] duration-500 ease-[cubic-bezier(.3,.7,.25,1)]',
          arrived ? (gathered ? GATHER[place] : 'opacity-100 translate-y-0 rotate-0') : 'opacity-0 -translate-y-8 -rotate-3',
        )}
        style={paper(stock)}
      >
        <p className="text-sm">{title}</p>
        {children}
        {stamp}
      </div>
    </div>
  );
}

// `inverse` for a dark band: the track under the job is drawn in its ink
export function JobLifecycle({ jobNumber, inverse = false }) {
  const stageRef = useRef(null);
  const inView = usePlayOnView(stageRef, 0.35);
  const [instant] = useState(prefersReducedMotion);
  const [step, setStep] = useState(0); // stages reached so far, 0 to STAGES.length
  const [finale, setFinale] = useState('none'); // none, gathered, flown

  useEffect(() => {
    if (!inView) {
      if (!instant) {
        setStep(0);
        setFinale('none');
      }
      return undefined;
    }
    // Reduced motion: the finished job, every paper on it, marked sent
    if (instant) {
      setStep(STAGES.length);
      return undefined;
    }
    const sendAt = START + (STAGES.length - 1) * STEP_MS;
    const timers = [
      ...STAGES.map((_, i) => setTimeout(() => setStep(i + 1), START + i * STEP_MS)),
      setTimeout(() => setFinale('gathered'), sendAt + 250),
      setTimeout(() => setFinale('flown'), sendAt + 250 + GATHER_MS),
    ];
    return () => timers.forEach(clearTimeout);
  }, [inView, instant]);

  const reached = (key) => STAGES.findIndex((s) => s.key === key) < step;
  const status = step ? STAGES[step - 1].status : 'new';
  const gathered = finale !== 'none';

  return (
    <div ref={stageRef} className="flex flex-col gap-6">
      {/* The job, and everything that arrives on it */}
      <div className="relative rounded-highlight bg-surface text-foreground p-5 sm:p-6 flex flex-col gap-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Logo className="w-8 h-8 shrink-0" alt="" />
            <p className="text-lg leading-tight">Job {jobNumber}</p>
          </div>
          <span
            className={cn(
              'shrink-0 rounded-pill px-3 py-1 text-sm transition-colors duration-500',
              status === 'xero' ? 'bg-moss-soft text-moss' : status === 'new' ? 'bg-mist text-muted-foreground' : getStatusColor(status),
            )}
            aria-live="polite"
          >
            {STATUS_LABEL[status]}
          </span>
        </div>

        {/* Clipped, so the papers leave the card rather than widen the page */}
        <div className="relative overflow-hidden -m-2 p-2">
          <div
            className={cn(
              'grid sm:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4',
              !instant && 'transition-[transform,opacity] ease-in',
              finale === 'flown' ? 'translate-x-[115%] opacity-0' : 'translate-x-0 opacity-100',
            )}
            style={{ transitionDuration: `${FLY_MS}ms` }}
            aria-hidden={finale === 'flown'}
          >
            <Doc title="Brief" stock="wheat" place={0} gathered={gathered} arrived={reached('brief')} instant={instant}>
              {[40, 55, 30].map((w, i) => (
                <span key={i} className="flex flex-col gap-1.5" aria-hidden="true">
                  <span className="h-1.5 rounded-pill" style={{ ...ink(35), width: `${w}%` }} />
                  <span className="h-6 rounded-cards" style={ink(12)} />
                </span>
              ))}
            </Doc>

            <Doc title="Shot list" stock="tide" place={1} gathered={gathered} arrived={reached('treatment')} instant={instant}>
              <span className="grid grid-cols-2 gap-1.5">
                {['roadside', 'snow', 'rain', 'stable'].map((name) => (
                  <span key={name} className="aspect-video rounded-small overflow-hidden">
                    <Reference name={name} className="w-full h-full block" />
                  </span>
                ))}
              </span>
            </Doc>

            <Doc title="Suppliers" stock="sage" place={2} gathered={gathered} arrived={reached('suppliers')} instant={instant}>
              <ul className="flex-1 flex flex-col justify-center gap-3" aria-label="Hires the shot list needs">
                {HIRES.map((name, i) => (
                  <li key={name} className="flex items-center justify-between gap-2">
                    <ul className="flex"><GearTag name={name} hire /></ul>
                    <Check
                      className={cn('w-4 h-4 text-moss transition-opacity duration-300', reached('suppliers') ? 'opacity-100' : 'opacity-0')}
                      style={{ transitionDelay: reached('suppliers') && !instant ? `${500 + i * 250}ms` : '0ms' }}
                      aria-hidden="true"
                    />
                  </li>
                ))}
              </ul>
            </Doc>

            <Doc
              title="Estimate"
              stock="terracotta"
              place={3}
              onTop
              gathered={gathered}
              arrived={reached('estimate')}
              instant={instant}
              stamp={(
                <span
                  className={cn(
                    'absolute right-3 top-3 rounded-pill border-2 border-foreground bg-background text-foreground px-3 py-1 text-sm font-medium',
                    !instant && 'transition-[transform,opacity] duration-300 ease-out',
                    reached('booked') ? 'opacity-100 scale-100 -rotate-6' : 'opacity-0 scale-150 rotate-0',
                  )}
                >
                  Booked
                </span>
              )}
            >
              {/* Labour, equipment, the treatment's hires (moss), expenses, hidden costs */}
              {[
                [78, false],
                [64, false],
                [50, true],
                [56, false],
              ].map(([w, hires], i) => (
                <Row key={i} shown={reached('estimate')} index={i} instant={instant}>
                  <span
                    className={cn('h-1.5 rounded-pill', hires && 'bg-moss-soft')}
                    style={hires ? { width: `${w}%` } : { ...ink(40), width: `${w}%` }}
                    aria-hidden="true"
                  />
                </Row>
              ))}
              <Row shown={reached('estimate')} index={4} instant={instant} className="opacity-70">
                <span className="flex items-center gap-1.5 w-full" aria-label="Hidden costs, not on the client's copy">
                  <EyeOff className="w-3 h-3 shrink-0" />
                  <span className="h-px flex-1 border-t border-dashed border-current opacity-50" />
                </span>
              </Row>
              <Row shown={reached('estimate')} index={5} instant={instant} className="mt-auto border-t border-[color-mix(in_srgb,currentColor_25%,transparent)] pt-2 justify-end">
                <span className="h-2 w-16 rounded-pill" style={ink(80)} aria-hidden="true" />
              </Row>
            </Doc>

          </div>

          {/* Where they went */}
          <div
            className={cn(
              'absolute inset-0 flex flex-col items-center justify-center gap-3 text-center',
              !instant && 'transition-opacity duration-500 delay-300',
              finale === 'flown' ? 'opacity-100' : 'opacity-0 pointer-events-none',
            )}
            aria-live="polite"
          >
            <span className="w-14 h-14 rounded-full bg-inverse text-inverse-foreground flex items-center justify-center">
              <Send className="w-6 h-6" />
            </span>
            <p className="text-2xl font-light">Sent to Xero for payment.</p>
          </div>
        </div>

      </div>

      {/* The life of the job, as a track */}
      <ol className="grid grid-cols-7 gap-1 sm:gap-2" aria-label="The job's life">
        {STAGES.map((stage, i) => {
          const done = i < step;
          return (
            <li key={stage.key} className="flex flex-col gap-2 min-w-0">
              <span className={cn('h-1.5 rounded-pill transition-colors duration-500', done ? (stage.key === 'xero' ? (inverse ? 'bg-moss-soft' : 'bg-moss') : (inverse ? 'bg-inverse-foreground' : 'bg-foreground')) : (inverse ? 'bg-inverse-foreground/15' : 'bg-foreground/15'))} />
              <span className={cn('text-[11px] sm:text-sm truncate transition-colors duration-500', done ? (inverse ? 'text-inverse-foreground' : 'text-foreground') : (inverse ? 'text-inverse-muted' : 'text-muted-foreground'))}>
                {stage.label}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
